/* eslint-disable max-classes-per-file */

import { useMemo } from "react";
import { createDebounceWithHandler } from "../../debouncer";
import { LoggingHandler } from "../logging/LoggingContext";

const KEY = "group-expand.";
const MAX_VALUES = 100;
interface ColumnCollapsedState {
    columnId: string;
    groupIds: string[];
}

function hashCode(str: string) {
    let hash = 0;
    for (let i = 0, len = str.length; i < len; i++) {
        // eslint-disable-next-line no-bitwise
        hash = (hash << 5) - hash + str.charCodeAt(i);
        // eslint-disable-next-line no-bitwise
        hash |= 0;
    }
    return hash.toString();
}

export default class TicketColumnState {
    public static useColumnState() {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        return useMemo(() => new TicketColumnState(), []);
    }

    private states?: Record<string, Set<string>>;

    private saveBouncer?: (() => void);

    private constructor() {
        //
    }

    public setDashboardId(dashboardId: string) {
        const storageKey = KEY + dashboardId;
        this.states = undefined;

        this.saveBouncer = createDebounceWithHandler(500, () => {
            if (!this.states) {
                return;
            }
            const entries = Object.entries(this.states);
            if (entries.isEmpty) {
                localStorage.removeItem(storageKey);
            } else {
                const data = entries.map(([columnId, groupIds]) => ({
                    columnId,
                    groupIds: Array.from(groupIds).limit(MAX_VALUES),
                } as ColumnCollapsedState));
                localStorage.setItem(storageKey, JSON.stringify(data));
            }
        });

        LoggingHandler.instance.nextTick(async () => {
            const data = JSON.parse(localStorage.getItem(storageKey) || "[]") as ColumnCollapsedState[];
            this.states = data.toIdMap("columnId", row => new Set(row.groupIds));
        });
    }

    public isCollapsed(columnId: string, groupId: string) {
        return this.states?.[columnId]?.has(hashCode(groupId));
    }

    public expand(columnId: string, groupId: string) {
        if (!this.states) {
            return;
        }

        const column = this.states[columnId];
        if (!column) {
            return;
        }
        column.delete(hashCode(groupId));
        if (column.size === 0) {
            delete this.states[columnId];
        }
        this.saveBouncer?.();
    }

    public collapse(columnId: string, groupId: string) {
        if (!this.states) {
            return;
        }
        const entries = this.states;
        entries[columnId] = entries[columnId] ?? new Set();
        entries[columnId].add(hashCode(groupId));
        this.saveBouncer?.();
    }
}
